import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BeneficiaryFinalizeCard from './BeneficiaryFinalizeCard';
import { BottomBar } from '../../BottomBar';
import { BeneficiaryLandingProps } from '../../Beneficiary/BeneficiaryLanding';
import {
  ReportQuestion,
  getQuestionsFromAnswersArray,
  updateAnswerState,
  getFinalizeMarkerQuestionId,
} from '../utils';
import { Answer } from 'donor/portal/pages/FeedbackDashboard/Feedback/api';
import MakeEditsModal, {
  MakeEditsModalProps,
} from '../../../../NPOFeedback/MakeEdits/MakeEditsModal';
import { useClient } from 'shared/apollo';
import {
  UpdateReportResponse,
  updateIndicatorAnswer,
} from '../../ImpactIndicator/api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  FeedbackQuestion,
  FeedbackQuestionsById,
} from '../../../../NPOFeedback/MakeEdits/api';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import { Report } from 'shared/components/WelcomeBack/utils';

interface FinalizeBeneficiariesProps extends BeneficiaryLandingProps {
  report: Report;
  loadingAnswers: boolean;
  answers: Answer[];
  questions: ReportQuestion[];
  reloadBeneficiaries: () => unknown;
}

const FinalizeBeneficiaries: React.FC<FinalizeBeneficiariesProps> = ({
  reportId,
  report,
  beneficiaries,
  question,
  questions: q,
  answers,
  loadingAnswers,
  programName = '',
  beneficiariesLoading,
  beneficiariesRefetch,
  reloadAnswers,
  onNextSection,
  onPreviousSection,
}) => {
  const client = useClient();
  const [requestState, makeRequest] = useGraphQLRequest<UpdateReportResponse>();
  const [selectedSection, setSelectedSection] = useState<
    MakeEditsModalProps['selectedSection'] | undefined
  >(undefined);
  const [questions, setQuestions] = useState<FeedbackQuestionsById | undefined>(
    undefined,
  );

  useEffect(() => {
    setQuestions(getQuestionsFromAnswersArray(q, answers));
  }, [q, answers]);

  const handleAnswerUpdate = useCallback(
    (
      questionId: number,
      answer: string,
      questionText: string,
      questionType: FeedbackQuestion['questionType'],
    ) => {
      setQuestions(q => {
        return updateAnswerState({
          answer: answer,
          questionId,
          questionText,
          questions: q,
          questionType,
        });
      });
    },
    [setQuestions],
  );

  useEffect(() => {
    if (requestState.type === 'REQUEST_SUCCESS') {
      reloadAnswers();
      onNextSection();
    }
  }, [requestState.type, reloadAnswers, setSelectedSection, onNextSection]);

  const handleSaveAnswer = useCallback(
    (questionId: number, answerId: number | undefined, answer: string) => {
      if (!client) return;
      makeRequest(
        updateIndicatorAnswer(
          client,
          answerId,
          true,
          questionId,
          reportId,
          answer,
        ),
      );
    },
    [client, reportId],
  );

  const handleOpen = useCallback(() => {
    setSelectedSection({
      question: {
        questionId: question?.dbId || 60001,
        questionText: question?.text || 'Beneficiaries section',
      },
      section: {
        label: 'Beneficiaries',
        sectionId: 'beneficiaries',
      },
    });
  }, [setSelectedSection, question]);

  const handleCompleted = useCallback(() => {
    // reloadAnswers();
    beneficiariesRefetch();
    setSelectedSection(undefined);
  }, [reloadAnswers, setSelectedSection, beneficiariesRefetch]);

  const handleNext = useCallback(() => {
    const questionId = getFinalizeMarkerQuestionId(3, report.stage);
    if (!questionId) return;
    handleSaveAnswer(questionId, undefined, 'Completed');
  }, [handleSaveAnswer, report.stage]);

  const handleClose = useCallback(() => {
    setSelectedSection(undefined);
  }, [setSelectedSection]);

  if (!questions) {
    return <TrueImpactLoading />;
  }

  return (
    <>
      <BeneficiaryFinalizeCard
        beneficiaries={beneficiaries}
        handleOpen={handleOpen}
        isLoading={beneficiariesLoading || loadingAnswers}
      />
      {selectedSection && (
        <MakeEditsModal
          isFinalizedUI={true}
          programName={programName}
          isError={false}
          isLoading={false}
          feedback={[]}
          report={report}
          feedbackByQuestionId={{}}
          handleAnswerUpdate={handleAnswerUpdate}
          reloadAnswers={reloadAnswers}
          markAsCompleted={handleCompleted}
          onClose={handleClose}
          isAnswersLoading={loadingAnswers}
          open={true}
          questions={questions}
          reportId={reportId}
          reportYear={report.year}
          selectedSection={selectedSection}
        />
      )}
      <BottomBar
        disabled={false}
        hideBack={false}
        hideNext={false}
        handleBack={onPreviousSection}
        handleNext={handleNext}
        backTitle="Back"
        nextTitle="Save & Continue"
      />
    </>
  );
};

export default FinalizeBeneficiaries;
